import React, { useCallback, useContext } from 'react';
import { View } from 'react-native';
import { connect } from 'react-redux';

import { Button } from '../button/Button';
import { app as appActions } from '../../actions';
import Dialog from '../dialog/Dialog';
import { ListContext } from '../../containers';
import Picker from '../picker/Picker';
import { useUserContext } from '../../../common/utils';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import {
	SettingsRow,
	SettingsSection,
} from '../settings/SettingsSection';

export const defaultPaginationValues = [10, 20, 50, 100, 200, 500];

const ListSettings = ({
	openDialog,
	assembler,
}) => {
	const listContext = useContext(ListContext);

	return (
		<>
			<Button
				onPress={() => openDialog(({ closeDialog }) => (
					<SettingsDialog
						listContext={listContext}
						closeDialog={closeDialog}
						assembler={assembler}
					/>
				))}
				label="Settings"
			/>
			<Dialog />
		</>
	);
};

const SettingsDialog = ({
	listContext,
	closeDialog,
	assembler,
}) => {
	const { userSettings, modifyUserSetting } = useUserContext();
	const styles = useThemedStyles(createStyles);

	const {
		listId,
		changePageLimit,
		changeSort,
		changeSortDirection,
	} = listContext;

	const setSortBy = useCallback(value => {
		return modifyUserSetting({
			lists: {
				...(userSettings.lists || {}),
				[listId]: {
					...(userSettings.lists && userSettings.lists[listId] ? userSettings.lists[listId] : {}),
					sortBy: value,
				},
			},
		}).then(() => changeSort && changeSort(value));
	}, [changeSort, listId, modifyUserSetting, userSettings.lists]);

	const setSortDirection = useCallback(value => {
		return modifyUserSetting({
			lists: {
				...(userSettings.lists || {}),
				[listId]: {
					...(userSettings.lists && userSettings.lists[listId] ? userSettings.lists[listId] : {}),
					sortDirection: value,
				},
			},
		}).then(() => changeSortDirection && changeSortDirection(value));
	}, [changeSortDirection, listId, modifyUserSetting, userSettings.lists]);

	const setPerPage = useCallback(value => {
		return modifyUserSetting({
			lists: {
				...(userSettings.lists || {}),
				[listId]: {
					...(userSettings.lists && userSettings.lists[listId] ? userSettings.lists[listId] : {}),
					perPage: value,
				},
			},
		}).then(() => changePageLimit && changePageLimit(value));
	}, [changePageLimit, listId, modifyUserSetting, userSettings.lists]);

	return (
		<View>
			<SettingsSection
				title="View settings"
				style={styles.section}
			>
				<SettingsRow label="Items per page" style={styles.stackedRow} contentStyle={styles.stackedContent}>
					<Picker
						selectedValue={getListSetting('perPage', userSettings, listId)}
						onValueChange={value => setPerPage(Number(value))}
						options={defaultPaginationValues.map(item => {
							return {
								label: `Show ${item} per page`,
								value: item,
							};
						})}
						width="100%"
					/>
				</SettingsRow>

				{
					Boolean(assembler?.length) && (
						<>
							<SettingsRow label="Sort by" style={styles.stackedRow} contentStyle={styles.stackedContent}>
								<Picker
									selectedValue={getListSetting('sortBy', userSettings, listId)}
									onValueChange={setSortBy}
									options={assembler.map(item => {
										return {
											label: item.label,
											value: item.sortBy,
										};
									})}
									width="100%"
								/>
							</SettingsRow>
							<SettingsRow label="Sort direction" style={styles.stackedRow} contentStyle={styles.stackedContent}>
								<Picker
									selectedValue={getListSetting('sortDirection', userSettings, listId) === 1 ? 'asc' : 'desc'}
									onValueChange={v => setSortDirection(v === 'asc' ? 1 : -1)}
									options={['asc', 'desc'].map(item => {
										return {
											label: item === 'asc' ? 'Ascending' : 'Descending',
											value: item,
										};
									})}
									width="100%"
								/>
							</SettingsRow>
						</>
					)
				}
			</SettingsSection>

			<Button
				style={styles.doneButton}
				secondary={true}
				label="OK"
				onPress={closeDialog}
			/>
		</View>
	);
};

export const getListSetting = (setting, userSettings, listId) => {
	if (!userSettings || !listId || !userSettings.lists || !userSettings.lists[listId] || !userSettings.lists[listId][setting]) {
		// can be undefined
		return undefined;
	}

	return userSettings.lists[listId][setting];
};

// Label above a full-width picker: the dialog is too narrow for both on one line.
const createStyles = ({ ui: { space } }) => ({
	section: {
		marginTop: 0,
	},
	stackedRow: {
		flexDirection: 'column',
		alignItems: 'stretch',
	},
	stackedContent: {
		alignItems: 'stretch',
		marginTop: space.sm,
	},
	doneButton: {
		marginTop: 14,
	},
});

const mapDispatchToProps = dispatch => ({
	openDialog: component => dispatch(appActions.openDialog(component)),
});

export default connect(null, mapDispatchToProps)(ListSettings);
